/* @flow */

import React, { PureComponent } from 'react'
import { List } from 'immutable'
import Basic from './basic'
import type { BasicPropsType, BasicStateType } from './basic'

export type ValuesEntryType = {
  value: string | number | boolean,
  label: string,
  info?: string,
  disabled?: boolean,
  tabIndex?: number,
  OptionComponent?: PureComponent<*, *>
}
export type ValuesType = Array<ValuesEntryType>

type CheckboxOptionPropsType = BasicPropsType & {
  id: string,
  name: string,
  value: string | number,
  checked: boolean,
  disabled?: boolean,
  onChange?: (boolean | List<string | number>) => void,
  optionInputClassName: string,
  optionLabelClassName: string,
  optionSpanClassName: string,
  optionInfoClassName: string,
  labelClassName?: string,
  directionClassName?: string,
  selectedValues: ValuesType,
  multiple?: boolean
}

class CheckboxOption extends PureComponent<CheckboxOptionPropsType, {}> {
  props: CheckboxOptionPropsType

  getValue = (event: SyntheticInputEvent<any>) => {
    const checked = event.target.checked
    // $FlowFixMe: flow does not detect selectedValues
    const { value, onChange, selectedValues, multiple } = this.props

    if (multiple) {
      let result: List<string | number> = List.isList(selectedValues) ? selectedValues : List()
      if (checked) {
        if (!result.includes(value)) result = result.push(value)
      } else {
        const index = result.indexOf(value)
        if (index >= 0) result = result.delete(index)
      }
      if (onChange) onChange(result)
    } else {
      if (onChange) onChange(checked)
    }
  }

  id(): string {
    const { id, name, value } = this.props
    return `${id || name || ''}-${String(value)}`
  }

  checked(): boolean {
    const { selectedValues, multiple, value } = this.props
    if (multiple) {
      if (!List.isList(selectedValues)) return false
      // $FlowFixMe
      return selectedValues.includes(value)
    }
    return !!selectedValues
  }

  formField() {
    const { name, value, disabled, optionInputClassName, tabIndex } = this.props
    const checked = this.checked()
    return (
      <input
        id={this.id()}
        type="checkbox"
        className={optionInputClassName}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={this.getValue}
        tabIndex={tabIndex}
      />
    )
  }

  render(): React$Element<*> {
    const { label, info, optionLabelClassName, optionSpanClassName, optionInfoClassName, labelClassName } = this.props
    return (
      <label className={`${optionLabelClassName} ${labelClassName || ''}`}>
        {this.formField()}{' '}
        <span id={`${this.id()}-label`} className={optionSpanClassName}>
          {label}
        </span>
        {info && (
          <span id={`${this.id()}-info`} className={optionInfoClassName}>
            {info}
          </span>
        )}
      </label>
    )
  }
}

export type CheckboxPropsType = BasicPropsType & {
  value?: boolean | List<string | number>,
  onChange?: (boolean | List<string | number>) => void,
  values: ValuesType,
  multiple?: boolean,
  optionInputClassName?: string,
  optionLabelClassName?: string,
  optionSpanClassName?: string,
  optionInfoClassName?: string,
  optionGroupClassName?: string,
  directionVerticalClassName?: string,
  directionHorizontalClassName?: string,
  OptionComponent?: PureComponent<CheckboxOptionPropsType, {}>,
  direction?: string
}

export type CheckboxStateType = BasicStateType & {}

class Checkbox extends Basic<CheckboxPropsType, CheckboxStateType> {
  static defaultProps = {
    ...Basic.defaultProps,
    rootType: 'checkbox',
    optionInputClassName: 'atp-input__checkbox',
    optionLabelClassName: 'atp-input__label',
    optionSpanClassName: 'atp-input__label__text',
    optionInfoClassName: 'atp-input__label__info',
    optionGroupClassName: 'atp-input__checkboxes',
    directionVerticalClassName: 'atp-input--vertical',
    directionHorizontalClassName: 'atp-input--horizontal',
    OptionComponent: CheckboxOption,
    direction: 'vertical'
  }

  get multiple() {
    return this.props.multiple || this.props.values.length > 1
  }

  optionChecked(entryValue: string | number | boolean): boolean {
    // $FlowFixMe: flow does not know List.isList as array check
    const { value } = this.props
    if (this.multiple) {
      if (!List.isList(value)) return false
      return value.includes(entryValue)
    }
    return !!value
  }

  get empty() {
    // $FlowFixMe: flow does not know List.isList as array check
    const { value } = this.props
    return this.multiple ? !List.isList(value) || value.size == 0 : !value
  }

  formField(): ?React$Element<*> {
    const props = this.getProps()
    const {
      values,
      name,
      value,
      onChange,
      disabled,
      OptionComponent,
      optionInputClassName,
      optionInfoClassName,
      optionLabelClassName,
      optionSpanClassName,
      optionGroupClassName,
      directionVerticalClassName,
      directionHorizontalClassName,
      direction
    } = props
    const multiple = this.multiple
    return (
      <div
        className={[
          optionGroupClassName,
          direction == 'vertical' ? directionVerticalClassName : directionHorizontalClassName
        ].join(' ')}>
        {values.map((entry: ValuesEntryType, index) => {
          const EntryOptionComponent = entry.OptionComponent || OptionComponent
          if (EntryOptionComponent != CheckboxOption) {
            console.log(
              'DEPRECATED: Checkbox.optionChecked will be removed and checked has to be checked inside the custom OptionComponent'
            )
          }
          return (
            // $FlowFixMe empty error
            <EntryOptionComponent
              key={index}
              id={this.id}
              index={index}
              values={values}
              name={name}
              checked={this.optionChecked(entry.value)}
              onChange={onChange}
              selectedValues={value}
              multiple={multiple}
              optionInputClassName={optionInputClassName}
              optionLabelClassName={optionLabelClassName}
              optionSpanClassName={optionSpanClassName}
              optionInfoClassName={optionInfoClassName}
              {...entry}
              disabled={entry.disabled || disabled}
              tabIndex={entry.tabIndex || props.tabIndex}
            />
          )
        })}
      </div>
    )
  }
}

export default Checkbox
export { CheckboxOption }
